/* -----------------------------------------------------------------------------------------------------
  - Created: [30-01-2025] by [SREERAJ]
  - Modified: [YYYY-MM-DD] 
  - Description: This file contains site list
  -------------------------------------------------------------------------------------------------------
*/

import React, { useEffect, useState } from 'react'
import WorksiteSearch from '../WorksiteSearch/WorksiteSearch';
import WorksiteForm from '../WorksiteForm/WorksiteForm';
import CustomModal from '../../../../Components/custom_components/CustomModal/CustomModal';
import CustomTable from '../../../../Components/custom_components/CustomTable/CustomTable';
import CustomBottomPagination from '../../../../Components/custom_components/CustomBottomPagination/CustomBottomPagination';
import { get_worksite_by_id, get_worksite_with_pagination, worksite_creation, worksite_update } from '../../../../config/Worksite/siteApis';
import { handlePaginationEntries } from '../../../../utils/pagination';
import { ErrorToast, SuccessToast } from '../../../../utils/toast';
import { hasPrivilege } from '../../../../utils/privileges';
import { useSelector } from 'react-redux';
import Loader from '../../../../Components/Loader/Loader';
import { Icons } from '../../../../utils/GlobalIcons';
import ActionHistory from '../../../../Components/ActionHistory/ActionHistory';
import CustomTooltipWithoutRoundBorder from '../../../../Components/CustomActionButtonWithTooltip/CustomTooltipWithoutRoundBorder';
import { getStatusColors } from '../../../../utils/constants';
import { fetchHistoryData } from '../../../../config/HistoryService/historyService';


function WorksiteTable() {
    const privileges = useSelector((state) => state.privileges.privileges);
    // State management
    const [Listing, SetListing] = useState([]);
    const [pageNumber, setPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalItemPerPage, setTotalItemPerPage] = useState(25);
    const [pagination, setPagination] = useState({ start: 0, end: 0, totalEntries: 0 });
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [modalProps, setModalProps] = useState({ isOpen: false, id: '', formValues: {} });
    const [filterValues, setFilterValues] = useState({});
    const [refresh, setRefresh] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Loader state

    const [showHistory, setShowHistory] = useState(false); // history modal state
    const [historyData, setHistoryData] = useState([]);
    const [historyLoader, setHistoryLoader] = useState(false); // Loader state


    // Fetch Worksite ( with pagination)
    useEffect(() => {
        fetchWorksite();
    }, [pageNumber, totalItemPerPage, filterValues, refresh]);

    const fetchWorksite = async () => {
        const formData = new FormData();

        formData.append('page', pageNumber);
        formData.append('pageItemcount', totalItemPerPage);

        Object.keys(filterValues).forEach(key => formData.append(key, filterValues[key]));

        try {
            setIsLoading(true)
            const response = await get_worksite_with_pagination(formData);
            if (response?.status === "success") {
                const data = response.data;
                SetListing(data.data);
                // Calculate pagination details
                setTotalPages(data.last_page);
                setPagination(handlePaginationEntries(data.from, data.to, data.total));

            } else {
                SetListing([]);
            }
        } catch (error) {
            console.error("Error fetching projects:", error);
            SetListing([]);
        } finally {
            setIsLoading(false)
        }
    };


    // Handle search filter change
    const handleSearchFilter = (filterValues) => {
        setFilterValues(filterValues);
    };

    // Handle modal for Add/Edit/View
    const handleModal = async (isOpen, worksiteId, actionType) => {
        if (actionType === 'view' && worksiteId) {
            await fetchWorksiteDetails(worksiteId);
            setIsViewModalOpen(true);
        } else if (worksiteId) {
            await fetchWorksiteDetails(worksiteId);
        } else {
            setModalProps({
                isOpen,
                id: '',
                formValues: {
                    WD_WorksiteName: '',
                    WD_SiteCode: '',
                    WD_Owner: [],
                    WD_Description: '',
                    WD_ParentWorksiteId: '', //PASS 0 WHEN NO PARENT SITE BY DEFAULT
                    WD_ProjectId: '',
                    WD_Location: '',
                    WD_status: 1
                },
            });
        }
    };

    // Fetch project details for view/edit
    const fetchWorksiteDetails = async (worksiteId) => {
        try {
            const response = await get_worksite_by_id({ worksiteID: worksiteId });
            if (response?.status === "success") {
                const data = response.data;
                setModalProps({
                    isOpen: true,
                    id: data?.WD_Id,
                    formValues: {
                        WD_WorksiteName: data?.WD_WorksiteName,
                        WD_SiteCode: data?.WD_SiteCode,
                        WD_Owner: JSON.parse(data?.WD_Owner || '[]')?.map(Number), // FOR CONVERT THE JSON INTO ARRAY WITH NUMBER ITEMS
                        WD_Description: data?.WD_Description,
                        WD_ParentWorksiteId: data?.WD_ParentWorksiteId,
                        WD_ProjectId: data?.WD_ProjectId,
                        WD_Location: data?.WD_Location,
                        WD_status: data?.WD_status,
                    }
                });
            }
        } catch (error) {
            console.error("Error fetching  details:", error);
        }
    };

    // Handle saving worksite data (Add/Edit)
    const handleWorksiteSave = async (values, worksiteId) => {

        try {
            setIsButtonDisabled(true);
            const apiFunction = worksiteId ? worksite_update : worksite_creation;
            if (worksiteId) values.worksiteID = worksiteId;

            const response = await apiFunction(values);
            if (response?.data?.status === "success") {
                SuccessToast(response?.data?.message || "Operation Successful");
                closeModal();
                setRefresh(!refresh);
            } else {
                ErrorToast(response?.data?.message || "Something went wrong!");
            }
        } catch (error) {
            console.error("Error saving project:", error);
        } finally {
            setIsButtonDisabled(false);
        }
    };

    // Close modal
    const closeModal = () => {
        setModalProps({ ...modalProps, isOpen: false });
        setIsViewModalOpen(false);
    };

    // Define the required permission actions
    const PERMISSION_ACTIONS = {
        view: 4,
        edit: 5,
        history: 6,
        add: 3,
    };

    // Map permissions to boolean values based on privileges
    const userPermissions = {
        view: hasPrivilege(privileges, "Work site", 2, PERMISSION_ACTIONS.view),
        edit: hasPrivilege(privileges, "Work site", 2, PERMISSION_ACTIONS.edit),
        history: hasPrivilege(privileges, "Work site", 2, PERMISSION_ACTIONS.history),
        add: hasPrivilege(privileges, "Work site", 2, PERMISSION_ACTIONS.add),
    };
    // / Function to check if any action-related permissions are granted
    const hasActionPermissions = userPermissions.view || userPermissions.edit || userPermissions.history;

    // Define columns for table
    const columns = [
        { title: "#", key: "key" },
        { title: "Name", key: "WD_WorksiteName" },
        { title: "Code", key: "WD_SiteCode" },
        { title: "Main Site", key: "prent" },
        { title: "Project", key: "project" },
        { title: "Location", key: "WD_Location" },
        {
            title: "Status",
            key: "WD_status",
            render: (_, record) => {
                //  actual status text
                const statusText = record?.WD_status || "Unknown";

                const statusCode = statusText.toLowerCase() === "active" ? 1 : statusText.toLowerCase() === "inactive" ? 2 : '';
                const style = getStatusColors(statusCode);

                return (
                    <span className="status-badge" style={style}>
                        {statusText}
                    </span>
                );
            },
        },
        { title: "Actions", key: "actions", render: (text, row) => customActions(row) },
    ].filter(column => column.key !== "actions" || hasActionPermissions); //removes the "Actions" column when hasActionPermissions is false


    // Render action icons
    const customActions = (row) => (
        <div className="action-container">

            {userPermissions.view &&
                <CustomTooltipWithoutRoundBorder title="View ">
                    <Icons.View className="view-action-icon" onClick={() => handleModal(true, row?.id, 'view')} />
                </CustomTooltipWithoutRoundBorder>
            }
            {userPermissions.edit &&
                <CustomTooltipWithoutRoundBorder title="Edit">
                    <Icons.Edit className="edit-action-icon" onClick={() => handleModal(true, row?.id)} />
                </CustomTooltipWithoutRoundBorder>
            }

            {userPermissions.history && <CustomTooltipWithoutRoundBorder title="History">
                <Icons.History className="history-action-icon" onClick={() => handleHistoryModal(true, row?.id)} />
            </CustomTooltipWithoutRoundBorder>}

        </div>
    );

    // Map project data to rows for the table
    const rows = () => Listing?.map((item, index) => ({
        key: pageNumber * totalItemPerPage - (totalItemPerPage - (index + 1)),
        id: item.WorksiteID,
        WD_WorksiteName: item.worksiteName,
        WD_SiteCode: item.worksiteCode,
        WD_Location: item.WorksiteLocation,
        WD_Owner: item.WD_Owner,
        WD_status: item.StatusName,
        prent: item.ParentWorksiteName,
        project: item.ProjectName

    }));

    // HISTORY BUTTON CLICK AND API CALL
    const handleHistoryModal = async (open, id) => {
        setShowHistory(open);

        if (open) {
            setHistoryLoader(true);
            try {

                const response = await fetchHistoryData('worksite', id);
                if (response?.status === "success") {

                    //  Transform API response for  table usage
                    const formatted = response?.data?.map((item, index) => ({
                        id: item.id,
                        worksite_name: item.worksite_name || "-",
                        changedBy: item.changed_by_name || "Unknown",
                        changedAt: item.changed_at || "-",
                        changeDescription: item.change_description || "-",
                    }));

                    setHistoryData(formatted || []);
                } else {
                    setHistoryData([]);
                }
            } catch (error) {
                console.error("Error fetching project history:", error);
            } finally {
                setHistoryLoader(false);
            }
        } else {
            setHistoryData([]);
        }
    };


    // Handle pagination changes
    const handlePageChange = (pageNumber) => {
        setPageNumber(pageNumber)
    };
    const handleItemPageCountChange = (pageItemCount) => setTotalItemPerPage(pageItemCount);

    return (
        <>
            <div className='worksite-management-table-container'>
                <WorksiteSearch
                    tableTitle={`Worksites`}
                    isAddEditModalOpen={modalProps.isOpen}
                    handleAddEditModal={handleModal}
                    searchFilter={handleSearchFilter}
                    isAddPermission={userPermissions?.add}

                    on_change_value={handlePageChange}
                    on_page_count_update={handleItemPageCountChange}
                />
                <div>
                    <CustomTable columns={columns} rows={isLoading ? [] : rows()} renderActions={customActions} checkboxEnabled={false} isLoading={isLoading} />
                </div>
                <div className='bottom-container'>
                    {isLoading ? <Loader /> : <CustomBottomPagination
                        items_per_page={totalItemPerPage}
                        current_page={pageNumber}
                        total_page_count={totalPages}
                        on_change_value={handlePageChange}
                        on_page_count_update={handleItemPageCountChange}
                        pagination={pagination}

                    />}
                </div>
            </div>

            {/* WORKSITE ADD/EDIT MODAL */}
            {modalProps.isOpen && <CustomModal
                open={modalProps.isOpen}
                handleClose={closeModal}
                title={isViewModalOpen ? "View Site" : modalProps.id ? "Edit Site" : "Create Site"}
                title_other=""
                body={
                    <WorksiteForm
                        isDisabled={isViewModalOpen}
                        isButtonDisabled={isButtonDisabled}
                        id={modalProps.id}
                        formValues={modalProps.formValues}
                        handleSave={handleWorksiteSave}

                    />
                }
                maxWidth="800px"
            />}

            {showHistory && <CustomModal
                open={showHistory}
                handleClose={() => handleHistoryModal(false)}
                title="Worksites History"
                maxWidth="75%"
                body={
                    historyLoader ? (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                            <Loader />
                        </div>
                    ) : (
                        <ActionHistory
                            title=""
                            data={historyData}
                            columns={[
                                { key: "worksite_name", label: "Worksite Name" },
                                { key: "changedBy", label: "Changed By" },
                                { key: "changedAt", label: "Changed At" },
                                { key: "changeDescription", label: "Change Description" },
                            ]}
                            noDataMessage="No change history available."
                        />

                    )
                }
            />}
        </>
    )
}

export default WorksiteTable